import { P } from "@angular/cdk/keycodes";
import { Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { GoogleMap } from "@angular/google-maps";
import { ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { ChartsService } from "src/app/shared/charts.service";
import { UiService } from "src/app/ui/ui.service";

@Component({
  selector: "app-heat-map",
  templateUrl: "./heat-map.component.html",
  styleUrls: ["./heat-map.component.scss"],
})
export class HeatMapComponent implements OnInit {
  filterForm!: FormGroup;
  mapOptions!: google.maps.MapOptions;
  @ViewChild("googleMap") googleMap!: GoogleMap;
  mapData: any;
  heatMap!: google.maps.visualization.HeatmapLayer;
  private onComponentDestroy = new Subject();

  constructor(
    private route: ActivatedRoute,
    private ChartsService: ChartsService,
    private NotificationService: NotificationService,
    private SessionService: SessionService,
    private uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.filterForm = new FormGroup({
      startDate: new FormControl(this.route.snapshot.data.start),
      endDate: new FormControl(this.route.snapshot.data.end),
    });

    const usCenter = {
      lat: 39.8282,
      lng: -98.5795,
    };

    this.mapOptions = {
      center: usCenter,
      zoom: 5,
      streetViewControl: false,
      mapTypeControl: true,
      minZoom: 0,
      maxZoom: 25,
    };
  }

  ngAfterViewInit() {
    this.initMap(this.route.snapshot.data.heatMap);

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        if (change.startDate < change.endDate) {
          this.filterForm.controls.endDate.value.setHours(23, 59, 59);

          const facilityIDs = this.SessionService.facilitiesValue?.length
            ? this.SessionService.facilitiesValue.map((facility) => {
                return facility.id;
              })
            : [];

          this.uiService
            .waitForEvent(
              this.ChartsService.getHeatMap(
                this.SessionService.currentAccountValue!.accountID,
                {
                  startdate: change.startDate,
                  enddate: this.filterForm.controls.endDate.value,
                  facility_id: facilityIDs,
                  type: "heat-map-2",
                },
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              (chartData) => {
                this.heatMap.setMap(null);

                this.initMap(chartData);
              },
              (err) => {
                this.NotificationService.openErrorSnackBar(
                  "Error retrieving map data",
                );
              },
            );
        }
      });
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  initMap(chartData: any) {
    this.initMapData(chartData.data);

    this.heatMap = new google.maps.visualization.HeatmapLayer({
      data: this.mapData,
      radius: 30,
      opacity: 0.7,
      maxIntensity: 10000,
      dissipating: true,
    });

    this.heatMap.setMap(this.googleMap.googleMap!);
  }

  initMapData(mapData: any) {
    this.mapData = [];
    for (let data of mapData) {
      data.geoLoc = JSON.parse(data.location_geo);
      data.beaconLoc = JSON.parse(data.location_beacon);

      const tos = data.time_on_station ?? 5;
      const tosMinimize = tos / 5;

      const minVal = Math.max(tosMinimize, 1);
      const weightValue = Math.min(minVal, 100);

      let loc;
      if (data.geoLoc?.coordinates) {
        loc = data.geoLoc;
      } else if (data.beaconLoc?.coordinates) {
        loc = data.beaconLoc;
      }

      if (loc) {
        const latLng = new google.maps.LatLng(
          loc.coordinates[1],
          loc.coordinates[0],
        );

        this.mapData.push({
          location: latLng,
          weight: weightValue,
        });
      }
    }
  }
}
